import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    IconButton,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { motion } from 'framer-motion';
import type { ICampaignCategory } from '../../../../components/EsForm/CampaignCategorySelector';
import type { ContestTierRow, ContestWizardState } from '../types';
import { emptyWizardCategory } from '../../contestManage/contestCategoryWizardUtils';
import { reconcilePrizeStructureWithTiers } from '../awardPresets';
import { ContestTiersAwardsHelpButton } from '../ContestTiersAwardsHelpButton';
import { RichTextEditor } from '../../../../components/Common/RichTextEditor';
import { editorialHeadlineSx, editorialInputSx, editorialLabelSx, midnight } from '../midnightStyles';

const SUGGESTED_TIERS = [
    { label: 'Bronze', color: '#CD7F32' },
    { label: 'Silver', color: '#C0C0C0' },
    { label: 'Gold', color: '#FFD700' },
];

const tierTableInputSx = {
    width: '100%',
    minWidth: 0,
    '& .MuiInputBase-root': { width: '100%' },
    '& .MuiInput-input': {
        color: midnight.onSurface,
        textOverflow: 'clip',
        overflow: 'visible',
        whiteSpace: 'nowrap',
    },
};

function parseFeeValue(raw: string): number {
    if (raw.trim() === '') return 0;
    const n = parseFloat(raw);
    return Number.isNaN(n) ? 0 : n;
}

function isValidFeeInput(raw: string): boolean {
    return raw === '' || /^\d*\.?\d*$/.test(raw);
}

interface Props {
    state: ContestWizardState;
    onChange: (patch: Partial<ContestWizardState>) => void;
    onOpenAiAssist: () => void;
}

export function CategoryBuilderStep({ state, onChange, onOpenAiAssist }: Props) {
    const idx = state.editingCategoryIndex ?? 0;
    const categories = state.categories.length ? state.categories : [emptyWizardCategory()];
    const category = categories[idx] ?? emptyWizardCategory();
    const [editingFees, setEditingFees] = useState<Record<number, string>>({});
    const [editingSlots, setEditingSlots] = useState<Record<number, string>>({});

    useEffect(() => {
        setEditingFees({});
        setEditingSlots({});
    }, [idx, category.tier_fee?.length]);

    const setCategory = (next: ICampaignCategory, syncPrizeTiers = false) => {
        const copy = [...categories];
        copy[idx] = syncPrizeTiers ? reconcilePrizeStructureWithTiers(next) : next;
        onChange({ categories: copy, editingCategoryIndex: idx });
    };

    const tiers: ContestTierRow[] = (category.tier_fee || []).map(t => ({
        label: t.label,
        fee: t.fee,
        currency: t.currency || 'USD',
        slots: (t as ContestTierRow).slots,
        starts_at: t.starts_at ?? null,
        ends_at: t.ends_at ?? null,
    }));

    const isTiered = category.with_tiered_pricing !== false;

    const setPricingMode = (tiered: boolean) => {
        if (tiered) {
            setCategory({
                ...category,
                with_tiered_pricing: true,
                with_fee: true,
                tier_fee: category.tier_fee?.length
                    ? category.tier_fee
                    : [{ label: 'Standard', fee: category.fee || 49, currency: 'USD' }],
            });
            return;
        }
        const first = category.tier_fee?.[0];
        setCategory({
            ...category,
            with_tiered_pricing: false,
            with_fee: true,
            fee: category.fee ?? first?.fee ?? 49,
            tier_fee: category.tier_fee,
        });
    };

    const updateTier = (tierIndex: number, field: keyof ContestTierRow, value: string | number | null) => {
        const nextTiers = [...tiers];
        nextTiers[tierIndex] = { ...nextTiers[tierIndex], [field]: value } as ContestTierRow;
        setCategory(
            {
                ...category,
                with_tiered_pricing: true,
                tier_fee: nextTiers.map((t, i) => ({
                    ...(category.tier_fee?.[i] || {}),
                    label: t.label,
                    fee: typeof t.fee === 'number' ? t.fee : parseFeeValue(String(t.fee)),
                    currency: t.currency || 'USD',
                    starts_at: t.starts_at ?? null,
                    ends_at: t.ends_at ?? null,
                })),
            },
            true,
        );
    };

    const commitFeeEdit = (tierIndex: number) => {
        const raw = editingFees[tierIndex];
        if (raw === undefined) return;
        updateTier(tierIndex, 'fee', parseFeeValue(raw));
        setEditingFees(prev => {
            const next = { ...prev };
            delete next[tierIndex];
            return next;
        });
    };

    const commitSlotsEdit = (tierIndex: number) => {
        const raw = editingSlots[tierIndex];
        if (raw === undefined) return;
        const slots = raw.trim() === '' ? 0 : parseInt(raw, 10);
        updateTier(tierIndex, 'slots', Number.isNaN(slots) ? 0 : slots);
        setEditingSlots(prev => {
            const next = { ...prev };
            delete next[tierIndex];
            return next;
        });
    };

    const addTier = () => {
        setCategory(
            {
                ...category,
                tier_fee: [...(category.tier_fee || []), { label: '', fee: 0, currency: 'USD' }],
            },
            true,
        );
    };

    const removeTier = (tierIndex: number) => {
        const next = [...(category.tier_fee || [])];
        next.splice(tierIndex, 1);
        setCategory({ ...category, tier_fee: next }, true);
    };

    const applySuggestedTier = (label: string) => {
        if ((category.tier_fee || []).some(t => t.label === label)) return;
        setCategory(
            {
                ...category,
                tier_fee: [...(category.tier_fee || []), { label, fee: label === 'Gold' ? 99 : 49, currency: 'USD' }],
            },
            true,
        );
    };

    const addCategory = () => {
        onChange({
            categories: [...categories, emptyWizardCategory()],
            editingCategoryIndex: categories.length,
        });
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, gap: 2 }}>
                <Typography sx={{ ...editorialHeadlineSx, fontSize: { xs: 28, md: 40 }, color: midnight.onSurface }}>
                    Category Builder
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {categories.length > 1 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {categories.map((c, i) => (
                                <Button
                                    key={i}
                                    size="small"
                                    onClick={() => onChange({ editingCategoryIndex: i })}
                                    sx={{
                                        minWidth: 32,
                                        borderRadius: 999,
                                        bgcolor: i === idx ? midnight.primary : midnight.surfaceHighest,
                                        color: i === idx ? '#392700' : midnight.onSurfaceVariant,
                                        fontWeight: 700,
                                    }}>
                                    {i + 1}
                                </Button>
                            ))}
                        </Box>
                    )}
                    <Button onClick={addCategory} sx={{ color: midnight.primary, fontWeight: 700 }}>
                        + Category
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                    gap: 4,
                }}>
                <Box component={motion.div} layout sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Box>
                        <Typography sx={editorialLabelSx}>Category Name</Typography>
                        <TextField
                            fullWidth
                            placeholder="e.g. Cinematic Lifestyle Video"
                            value={category.category_name}
                            onChange={e => setCategory({ ...category, category_name: e.target.value })}
                            sx={editorialInputSx}
                        />
                    </Box>
                    <Box>
                        <Typography sx={editorialLabelSx}>Description</Typography>
                        <RichTextEditor
                            shellVariant="brandDashboard"
                            placeholder="Describe the requirements and creative vision for this specific category..."
                            value={category.category_description || ''}
                            onChange={category_description => setCategory({ ...category, category_description })}
                        />
                    </Box>

                    <Box
                        sx={{
                            bgcolor: midnight.surfaceLow,
                            p: 3,
                            borderRadius: '12px',
                        }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Box>
                                <Typography sx={{ ...editorialHeadlineSx, fontSize: 18, color: midnight.primary }}>
                                    Monetization Settings
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: midnight.onSurfaceVariant }}>
                                    Enable fees for creator submissions in this category.
                                </Typography>
                            </Box>
                            <Switch
                                checked={category.with_fee ?? false}
                                onChange={e => setCategory({ ...category, with_fee: e.target.checked })}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: midnight.primary },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        bgcolor: 'rgba(255,201,101,0.25)',
                                    },
                                }}
                            />
                        </Box>

                        {category.with_fee && (
                            <Box>
                                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                    <Button
                                        size="small"
                                        onClick={() => setPricingMode(false)}
                                        sx={{
                                            borderRadius: 999,
                                            px: 2,
                                            fontWeight: 700,
                                            fontSize: 12,
                                            bgcolor: !isTiered ? midnight.primary : midnight.surfaceHighest,
                                            color: !isTiered ? '#392700' : midnight.onSurface,
                                        }}>
                                        Flat fee
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => setPricingMode(true)}
                                        sx={{
                                            borderRadius: 999,
                                            px: 2,
                                            fontWeight: 700,
                                            fontSize: 12,
                                            bgcolor: isTiered ? midnight.primary : midnight.surfaceHighest,
                                            color: isTiered ? '#392700' : midnight.onSurface,
                                        }}>
                                        Tiered pricing
                                    </Button>
                                    {isTiered ? <ContestTiersAwardsHelpButton /> : null}
                                </Box>

                                {!isTiered ? (
                                    <TextField
                                        fullWidth
                                        label="Submission fee ($)"
                                        value={category.fee ?? ''}
                                        onChange={e => {
                                            const raw = e.target.value;
                                            if (raw !== '' && !isValidFeeInput(raw)) return;
                                            setCategory({
                                                ...category,
                                                with_tiered_pricing: false,
                                                fee: raw === '' ? 0 : parseFeeValue(raw),
                                            });
                                        }}
                                        sx={editorialInputSx}
                                    />
                                ) : (
                                    <Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                            <Typography sx={{ ...editorialLabelSx, mb: 0 }}>
                                                Tiered Pricing Matrix
                                            </Typography>
                                            <Button
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={addTier}
                                                sx={{ color: midnight.primary, fontWeight: 700, fontSize: 11 }}>
                                                Add Row
                                            </Button>
                                        </Box>
                                        <Table
                                            size="small"
                                            sx={{
                                                tableLayout: 'fixed',
                                                width: '100%',
                                                '& .MuiTableCell-root': { verticalAlign: 'middle' },
                                            }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell
                                                        sx={{
                                                            color: midnight.onSurfaceVariant,
                                                            border: 0,
                                                            width: '42%',
                                                            minWidth: 140,
                                                        }}>
                                                        Tier
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            color: midnight.onSurfaceVariant,
                                                            border: 0,
                                                            width: '22%',
                                                            minWidth: 72,
                                                        }}>
                                                        Fee ($)
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            color: midnight.onSurfaceVariant,
                                                            border: 0,
                                                            width: '18%',
                                                            minWidth: 64,
                                                        }}>
                                                        Slots
                                                    </TableCell>
                                                    <TableCell sx={{ border: 0, width: 48 }} />
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {tiers.map((tier, ti) => (
                                                    <TableRow key={ti} sx={{ bgcolor: midnight.surfaceContainer }}>
                                                        <TableCell sx={{ border: 0, pr: 1 }}>
                                                            <TextField
                                                                fullWidth
                                                                variant="standard"
                                                                placeholder="Tier name"
                                                                value={tier.label}
                                                                onChange={e => updateTier(ti, 'label', e.target.value)}
                                                                sx={tierTableInputSx}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ border: 0, px: 1 }}>
                                                            <TextField
                                                                fullWidth
                                                                variant="standard"
                                                                type="text"
                                                                inputMode="decimal"
                                                                placeholder="0"
                                                                value={
                                                                    editingFees[ti] !== undefined
                                                                        ? editingFees[ti]
                                                                        : String(tier.fee ?? '')
                                                                }
                                                                onChange={e => {
                                                                    const raw = e.target.value;
                                                                    if (!isValidFeeInput(raw)) return;
                                                                    setEditingFees(prev => ({ ...prev, [ti]: raw }));
                                                                    if (raw !== '' && !raw.endsWith('.')) {
                                                                        const n = parseFloat(raw);
                                                                        if (!Number.isNaN(n)) {
                                                                            updateTier(ti, 'fee', n);
                                                                        }
                                                                    }
                                                                }}
                                                                onBlur={() => commitFeeEdit(ti)}
                                                                sx={tierTableInputSx}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ border: 0, px: 1 }}>
                                                            <TextField
                                                                fullWidth
                                                                variant="standard"
                                                                type="text"
                                                                inputMode="numeric"
                                                                placeholder="—"
                                                                value={
                                                                    editingSlots[ti] !== undefined
                                                                        ? editingSlots[ti]
                                                                        : tier.slots
                                                                        ? String(tier.slots)
                                                                        : ''
                                                                }
                                                                onChange={e => {
                                                                    const raw = e.target.value;
                                                                    if (raw !== '' && !/^\d*$/.test(raw)) return;
                                                                    setEditingSlots(prev => ({ ...prev, [ti]: raw }));
                                                                    if (raw !== '') {
                                                                        const n = parseInt(raw, 10);
                                                                        if (!Number.isNaN(n)) {
                                                                            updateTier(ti, 'slots', n);
                                                                        }
                                                                    }
                                                                }}
                                                                onBlur={() => commitSlotsEdit(ti)}
                                                                sx={tierTableInputSx}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ border: 0, width: 48, p: 0.5 }}>
                                                            <IconButton size="small" onClick={() => removeTier(ti)}>
                                                                <DeleteOutlineIcon
                                                                    sx={{ color: midnight.onSurfaceVariant }}
                                                                />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {isTiered ? (
                        <Box
                            sx={{
                                bgcolor: midnight.surfaceLow,
                                p: 3,
                                borderRadius: '12px',
                            }}>
                            <Typography sx={{ ...editorialLabelSx, mb: 2 }}>Suggested Tiers</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                {SUGGESTED_TIERS.map(t => (
                                    <Button
                                        key={t.label}
                                        onClick={() => applySuggestedTier(t.label)}
                                        sx={{
                                            borderRadius: 999,
                                            px: 2,
                                            py: 1,
                                            bgcolor: midnight.surfaceHighest,
                                            color: midnight.onSurface,
                                            fontWeight: 700,
                                            fontSize: 13,
                                            border: '1px solid rgba(255,255,255,0.06)',
                                            '&:hover': { borderColor: 'rgba(255,201,101,0.4)' },
                                        }}>
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                bgcolor: t.color,
                                                mr: 1,
                                            }}
                                        />
                                        {t.label}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                    ) : null}

                    <Box
                        sx={{
                            p: 3,
                            borderRadius: '12px',
                            border: `1px solid ${midnight.outline}`,
                        }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <AutoAwesomeIcon sx={{ color: midnight.secondary }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: 700, mb: 0.5, color: midnight.onSurface }}>
                                    AI Assistant
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: midnight.onSurfaceVariant, mb: 2 }}>
                                    Answer a few questions and we will draft your category name, description, and tier
                                    fees.
                                </Typography>
                                <Button
                                    onClick={onOpenAiAssist}
                                    sx={{
                                        color: midnight.secondary,
                                        fontWeight: 700,
                                        fontSize: 11,
                                        letterSpacing: '0.15em',
                                        textTransform: 'uppercase',
                                        p: 0,
                                        minWidth: 0,
                                        '&:hover': {
                                            bgcolor: 'transparent',
                                            color: midnight.secondary,
                                            opacity: 0.85,
                                        },
                                    }}>
                                    Generate Draft →
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
